import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

// components
import PrimaryButton from "../../../../components/common/PrimaryButton";
import Spinner from "../../../../components/common/Spinner";
import Alert, { AlertType } from "../../../../components/common/WaffyAlert";
import { ALERT_MESSAGES } from "../../../../components/userProfile/securityCode/constant";
import {
  requestOtp2,
  validateOtpGeneric,
} from "../../../../setup/api/services/otp";
import { EOTPType } from "../../../../setup/api/services/otp/models";
import {
  getProfileByPhone,
  resetPinCode,
} from "../../../../setup/api/services/user";
import { IResetPinCodeRequestBody } from "../../../../setup/api/services/user/models";
import { ROUTES } from "../../../../setup/constants";
import {
  CONFIRM,
  CONTINUE,
  OTP_FORM,
  RESET_OTP_FORM,
  RESET_PHONE_NUMBER_FORM,
  RESET_SECURITY_CODE_FORM,
} from "../../constants";
import { OtpCodeForm } from "../../otpCode/otpCodeForm";
import { PhoneNumberForm } from "../../phoneNumber/PhoneNumberForm";
import { SecurityCodeForm } from "../../securityPinCode/securityCodeForm";
import { ERROR_OTP_VALIDATION } from "../../signup/constants";
import {
  validateOtpCode,
  validatePhoneNumber,
  validateSecurityPinCode,
} from "../../validation";
import { ERegionCode } from "../../validation/enum";
import FormHeading from "../formHeading";
import formModel from "./formModel";
import initialValues from "./initialValues";
// styles
import resetForm from "./resetForm.module.scss";

const ResetForm = () => {
  const navigate = useNavigate();

  const { formId, formField } = formModel;
  const steps = { phoneNumber: 1, otpCode: 2, pinCode: 3, confirmPinCode: 4 };

  const [error, setError] = useState<string>();
  const [currentStep, setCurrentStep] = useState<number>(steps.phoneNumber);
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [firstPinCode, setFirstPinCode] = useState<string>("");
  const [secondPinCode, setSecondPinCode] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState<any>({});
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [otpCode, setOtpCode] = useState<any>();

  const _handleSubmit = () => {
    if (currentStep === steps.phoneNumber) onSubmitPhoneNumber();
    else if (currentStep === steps.otpCode) onOtpCodeFormSubmit();
    else if (currentStep === steps.pinCode) handleNext();
    else onSubmitConfirmPinCode();
  };
  const handleNext = () => {
    setCurrentStep(currentStep + 1);
    setError(undefined);
  };

  const onOtpCodeFormSubmit = () => {
    if (profile && otpCode && validateOtpCode(otpCode)) {
      validateOtpGeneric({
        type: EOTPType.WAFFY_OTP,
        otp: Number.parseInt(otpCode),
        userId: profile?.id,
      })
        .then((res) => {
          if (res.data.valid) handleNext();
          else setError(ERROR_OTP_VALIDATION);
        })
        .catch(() => {
          setError(OTP_FORM.ERROR);
        });
    } else setError(ERROR_OTP_VALIDATION);
  };

  const onSubmitPhoneNumber = () => {
    if (validatePhoneNumber(`+${phoneNumber}`, ERegionCode.SA)) {
      getProfileByPhone({ phoneNumber: `+${phoneNumber}` })
        .then(async (result) => {
          setProfile(result.data);
          await requestOtp2({
            userId: String(result.data.id),
            notificationChannel: "SMS",
          }).catch((e) => console.error(e));
          setPhoneVerified(true);
          setCurrentStep(steps.otpCode);
        })
        .catch((err) => {
          setError(RESET_PHONE_NUMBER_FORM.ERROR);
        });
    } else setError(RESET_PHONE_NUMBER_FORM.ERROR);
  };

  const onSubmitConfirmPinCode = () => {
    if (
      validateSecurityPinCode(firstPinCode) &&
      validateSecurityPinCode(secondPinCode) &&
      secondPinCode === firstPinCode
    ) {
      const body: IResetPinCodeRequestBody = {
        otp: Number.parseInt(otpCode),
        newPinCode: firstPinCode,
        reNewPinCode: secondPinCode,
      };
      resetPinCode(profile.id, body)
        .then((result) => {
          if (result?.status === 200) {
            setTimeout(() => navigate(ROUTES.HOME), 500);
          } else {
            setError(ALERT_MESSAGES.UPDATE_FAILED);
          }
        })
        .catch(() => setError(ALERT_MESSAGES.UPDATE_FAILED));
    }
  };

  useEffect(() => {
    if (error && error !== "") {
      setTimeout(() => {
        setError("");
      }, 2000);
    }
  }, [error]);

  return (
    <div className={resetForm.container}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        onSubmit={_handleSubmit}>
        <Form className={resetForm.login_form} id={formId}>
          {error && (
            <Alert type={AlertType.ERROR} showIcon>
              {error}
            </Alert>
          )}

          {currentStep === steps.otpCode && (
            <div className={resetForm.pincode_container}>
              <FormHeading
                header={RESET_OTP_FORM.TITLE}
                subHeader={RESET_OTP_FORM.SUB_TITLE}
                boldText={phoneNumber}
              />
              <OtpCodeForm value={otpCode} key={0} onChange={setOtpCode} />
            </div>
          )}
          {currentStep === steps.phoneNumber && (
            <div className={resetForm.pincode_container}>
              <FormHeading
                header={RESET_PHONE_NUMBER_FORM.TITLE}
                subHeader={RESET_PHONE_NUMBER_FORM.SUB_TITLE}
                boldText={""}
              />
              <PhoneNumberForm
                value={phoneNumber}
                formField={formField}
                onChange={setPhoneNumber}
              />
            </div>
          )}
          {currentStep === steps.pinCode && (
            <div className={resetForm.pincode_container}>
              <FormHeading
                header={RESET_SECURITY_CODE_FORM.TITLE}
                subHeader={RESET_SECURITY_CODE_FORM.SUB_TITLE}
                boldText={""}
              />
              <SecurityCodeForm onChange={setFirstPinCode} />
            </div>
          )}
          {currentStep === steps.confirmPinCode && (
            <div className={resetForm.pincode_container}>
              <FormHeading
                header={RESET_SECURITY_CODE_FORM.TITLE}
                subHeader={RESET_SECURITY_CODE_FORM.SECOND_SUB_TITLE}
                boldText={""}
              />
              <SecurityCodeForm onChange={setSecondPinCode} />
            </div>
          )}
          <div className={resetForm.button_container}>
            <PrimaryButton
              id={`reset_pin_${
                currentStep === steps.confirmPinCode ? "confirm" : "continue"
              }`}
              disabled={phoneNumber === "" || loading}
              type="submit">
              {loading ? (
                <Spinner />
              ) : (
                `${currentStep === steps.confirmPinCode ? CONFIRM : CONTINUE}`
              )}
            </PrimaryButton>
          </div>
        </Form>
      </Formik>
    </div>
  );
};

export default ResetForm;
